import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCertificate,
  faCheckCircle,
  faDownload,
  faShareNodes,
  faCalendarAlt,
  faArrowRight,
  faAward,
  faCircleExclamation,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import './MyCertificates.css';

const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
};

const MyCertificates = ({ userData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null); // index of the copied link

  const studentId = userData?.id;
  const fullName = userData?.fullName || '';

  // ---------- Data (same endpoint) ----------
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/student/certificates/${studentId}`);
        if (!cancelled) setData(response.data);
      } catch (err) {
        if (!cancelled) setError('We couldn’t load your certificates.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  // Newest first
  const certificates = useMemo(
    () =>
      [...(data?.mycertificates || [])].sort((a, b) => new Date(b.Date || 0) - new Date(a.Date || 0)),
    [data]
  );

  // Share: phone share sheet when available, otherwise copy the link
  const share = async (cert, index) => {
    const text = `${fullName ? `${fullName} earned` : 'I earned'} the RoboMind certificate: ${cert.ModuleName} 🎉`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'RoboMind certificate', text, url: cert.Link });
        return;
      }
      await navigator.clipboard.writeText(`${text}\n${cert.Link}`);
      setCopied(index);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // share sheet closed: nothing to do
    }
  };

  // ---------- Loading ----------
  if (loading || !studentId) {
    return (
      <div className="rmce" aria-busy="true">
        <div className="rmce-inner">
          <div className="rmce-skel rmce-skel--title" />
          <div className="rmce-grid">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rmce-skel rmce-skel--card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error || !data) {
    return (
      <div className="rmce">
        <div className="rmce-state">
          <FontAwesomeIcon icon={faCircleExclamation} className="rmce-state-icon is-error" />
          <h1 className="rmce-state-title">Something went wrong</h1>
          <p>{error || 'We couldn’t find your certificates.'} Please try again in a moment.</p>
          <button type="button" className="rmce-btn" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const count = Number(data.nbofcertificates) || certificates.length;

  return (
    <div className="rmce">
      <div className="rmce-inner">
        <header className="rmce-head">
          <div>
            <span className="rmce-eyebrow">My Certificates</span>
            <h1 className="rmce-title">
              Your <span>achievements</span>
            </h1>
          </div>
          <div className="rmce-summary">
            <span>
              <FontAwesomeIcon icon={faAward} /> <strong>{count}</strong> {count === 1 ? 'certificate' : 'certificates'}
            </span>
            <span>
              <FontAwesomeIcon icon={faCheckCircle} /> <strong>{data.nbofclasstaken || 0}</strong> classes completed
            </span>
          </div>
        </header>


        {certificates.length === 0 ? (
          <div className="rmce-empty">
            <span className="rmce-empty-icon">
              <FontAwesomeIcon icon={faCertificate} />
            </span>
            <h2>Your first certificate is on its way</h2>
            <p>Finish a module and your certificate will appear here, ready to download and share.</p>
            <Link to="/student/myclasses" className="rmce-btn">
              See my progress <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        ) : (
          <ul className="rmce-grid">
            {certificates.map((cert, i) => (
              <li key={`${cert.ModuleName}-${cert.Date}-${i}`} className="rmce-card">
                <div className="rmce-paper" aria-hidden="true">
                  <span className="rmce-paper-brand">RoboMind Academy</span>
                  <span className="rmce-paper-label">Certificate of completion</span>
                  <span className="rmce-paper-name">{fullName || 'Student'}</span>
                  <span className="rmce-seal">
                    <FontAwesomeIcon icon={faAward} />
                  </span>
                </div>

                <div className="rmce-body">
                  <h2 className="rmce-card-title">{cert.ModuleName}</h2>
                  {fmtDate(cert.Date) && (
                    <p className="rmce-date">
                      <FontAwesomeIcon icon={faCalendarAlt} /> Issued {fmtDate(cert.Date)}
                    </p>
                  )}

                  <div className="rmce-actions">
                    {cert.Link ? (
                      <>
                        <a href={cert.Link} target="_blank" rel="noopener noreferrer" className="rmce-btn rmce-btn--full">
                          <FontAwesomeIcon icon={faDownload} /> Download
                        </a>
                        <button type="button" className="rmce-share" onClick={() => share(cert, i)} aria-label={`Share ${cert.ModuleName} certificate`}>
                          <FontAwesomeIcon icon={copied === i ? faCheck : faShareNodes} />
                          {copied === i ? 'Copied' : 'Share'}
                        </button>
                      </>
                    ) : (
                      <p className="rmce-pending">Your certificate file is being prepared.</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MyCertificates;